import SubBanner from '@/components/common/Banner/SubBanner/SubBanner';
import useIsMobile from '@/hooks/useIsMobile';
import { useLocation, useNavigate } from 'react-router-dom';
import * as S from './style';
import infoBanner from '@/assets/img/banner/portfolio.png';
import { useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import ArrowLeft from '@/assets/img/icon/ArrowLeft.svg';
import ArrowRight from '@/assets/img/icon/ArrowRight.svg';
import Xbtn from '@/assets/img/icon/Xbtn.svg';
import { useGetPortfolioApi } from '@/api/basic';

const portIconImages: Record<string, string> = import.meta.glob(
    '@/assets/img/portfolio/icon/*.svg',
    { eager: true, import: 'default' }
) as Record<string, string>;
const portThumbnailImages: Record<string, string> = {
    ...(import.meta.glob('@/assets/img/portfolio/*.png', { eager: true, import: 'default' }) as Record<string, string>),
    ...(import.meta.glob('@/assets/img/portfolio/*.jpg', { eager: true, import: 'default' }) as Record<string, string>),
    ...(import.meta.glob('@/assets/img/portfolio/*.jpeg', { eager: true, import: 'default' }) as Record<string, string>),
};
const portDetailImages: Record<string, string> = import.meta.glob(
    '@/assets/img/portfolio/**/*.jpg',
    { eager: true, import: 'default' }
) as Record<string, string>;

const Portfolio = () => {
    // 데이터
    const { data: portfolioData } = useGetPortfolioApi();

    const currentList = portfolioData?.portfolio?.filter(p => p.portfolio_status === 'Current').map(p => ({
        id: p.portfolio_pk,
        name: p.portfolio_title,
        period: p.portfolio_period,
        icon: p.logo_image_url,
        thumbnail: p.portfolio_image_url,
        photos: p.photo_file.map((file: any) => file.file_path) || [],
        experiences: p.portfolio_content.replace(/<\/?[^>]+(>|$)/g, "").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').split('\n').filter(line => line.trim() !== ''),
    })) || [];

    const exitedList = portfolioData?.portfolio?.filter(p => p.portfolio_status === 'Exited').map(p => ({
        id: p.portfolio_pk,
        name: p.portfolio_title,
        period: p.portfolio_period,
        icon: p.logo_image_url,
        thumbnail: p.portfolio_image_url,
        photos: p.photo_file.map((file: any) => file.file_path) || [],
        experiences: p.portfolio_content.replace(/<\/?[^>]+(>|$)/g, "").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").split('\n').filter(line => line.trim() !== ''),
    })) || [];

    const [activeTab, setActiveTab] = useState<'current' | 'exited'>('current');
    const [openModal, setOpenModal] = useState<null | { id: string; title: string; photos: string[] }>(null);

    const filteredList = useMemo(() => 
        // 실제 데이터 연동 시, activeTab에 따라 데이터 소스 분기
         activeTab === 'current' ? currentList : exitedList
    , [activeTab, currentList, exitedList]);

	return (
		<main>
			<SubBanner subTitle="Portfolio" title="인수합병부터 부실채권까지, 경험으로 증명한 운용 능력으로 성공을 이루겠습니다." img={infoBanner} />
            <S.ConferenceContainer>
                <S.PortfolioTabs>
                    <S.PortfolioTabButton isActive={activeTab === 'current'} onClick={() => setActiveTab('current')}>
                        Current
                    </S.PortfolioTabButton>
                    <S.PortfolioTabButton isActive={activeTab === 'exited'} onClick={() => setActiveTab('exited')}>
                        Exited
                    </S.PortfolioTabButton>
                </S.PortfolioTabs>

                <S.PortfolioCardListWrap>
                    {filteredList.map((item) => (
                        <S.PortfolioCard key={item.id}>
                            <S.PortfolioCardLeft>
                                <S.PortfolioIconSlot bgImg={(item as any).icon} />
                                <S.PortfolioTitle>{item.name}</S.PortfolioTitle>
                                <S.PortfolioPeriod>
                                    운용 기간: {(item as any).period ?? ''}
                                </S.PortfolioPeriod>
                                <S.PortfolioDetailsList>
                                    {item.experiences.map((d, i) => (
                                        <S.PortfolioDetailsItem key={i}>
                                            {d.replace(/<\/?[^>]+(>|$)/g, "").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"')}
                                        </S.PortfolioDetailsItem>
                                    ))}
                                </S.PortfolioDetailsList>
                                <S.MorePhotoButton onClick={() => setOpenModal({ id: item.id, title: item.name, photos: (item as any).photos || [] })}>
                                    사진 더보기
                                </S.MorePhotoButton>
                            </S.PortfolioCardLeft>
                            <S.PortfolioCardRight>
                                <S.PortfolioImage bgImg={(item as any).thumbnail || infoBanner} />
                            </S.PortfolioCardRight>
                        </S.PortfolioCard>
                    ))}
                </S.PortfolioCardListWrap>
                {openModal && (
                    <S.ModalOverlay onClick={() => setOpenModal(null)}>
                        <S.ModalContent onClick={(e) => e.stopPropagation()}>
                            <S.ModalBody>
                                <S.ModalSwiperWrap>
                                    <S.ModalStage>
                                    <Swiper
                                        spaceBetween={16}
                                        slidesPerView={1}
                                        onSwiper={(swiper) => (window as any).__portfolio_swiper = swiper}
                                        observer
                                        observeParents
                                    >
                                        {openModal.photos.map((src, idx) => (
                                            <SwiperSlide key={idx}>
                                                <S.ModalImgEl src={src} alt="portfolio" />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                    <S.ModalNavButton side="left" onClick={(e) => { e.stopPropagation(); (window as any).__portfolio_swiper?.slidePrev(); }}>
                                        <img src={ArrowLeft} alt="prev" />
                                    </S.ModalNavButton>
                                    <S.ModalNavButton side="right" onClick={(e) => { e.stopPropagation(); (window as any).__portfolio_swiper?.slideNext(); }}>
                                        <img src={ArrowRight} alt="next" />
                                    </S.ModalNavButton>
                                    <S.ModalCloseButton onClick={(e) => { e.stopPropagation(); setOpenModal(null); }}>
                                        <img src={Xbtn} alt="close" />
                                    </S.ModalCloseButton>
                                    </S.ModalStage>
                                </S.ModalSwiperWrap>
                            </S.ModalBody>
                        </S.ModalContent>
                    </S.ModalOverlay>
                )}
            </S.ConferenceContainer>
		</main>
	);
};

export default Portfolio;
